import { faCheckCircle, faCircleNotch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { hasValue, logError, noValue } from "@repo/helpers";
import { TextBox } from "@repo/ui";
import { FC, useCallback, useEffect, useState } from "react";

import style from "./style.module.scss";
import { Props, ValidationResponse, ValidationResult } from "./types";

export const EntryTextWithValidation: FC<Props> = (props) => {
	const {
		id,
		disabled,
		readOnly,
		validationError,
		mappedComponent,
		valueChanged,
		componentValueChanged,
		customFunctions,
	} = props;

	if (!customFunctions || typeof customFunctions !== "function")
		logError("customFunctions is not a function.  Possibly wrong component type?");

	const { component, extensionPropertyValue, propertyValue: value } = mappedComponent;
	const [searching, setSearching] = useState(false);
	const [validationResults, setValidationResults] = useState<ValidationResult[]>([]);
	const validateFn = customFunctions(component.value);
	const validationResult = validationResults.find((r) => r.value === value);
	const { success, message } = validationResult ?? { success: undefined, message: undefined };

	const [manual, setManual] = useState(false);

	useEffect(() => {
		validate();
	}, []);

	useEffect(() => {
		updateValidation();
	}, [success, message]);

	useEffect(() => {
		if (!extensionPropertyValue && hasValue(validationResult)) updateValidation(true);
	}, [extensionPropertyValue, validationResult]);

	const updateValidation = useCallback(
		(dontEdit?: boolean) => valueChanged({ success, message }, `${id}Validation`, null, dontEdit || !manual),
		[valueChanged, success, message, id],
	);

	const onChange = useCallback(
		(value: string, id?: string, dontEdit?: boolean) => {
			setManual(true);
			componentValueChanged(value, id);
		},
		[componentValueChanged],
	);

	async function validate() {
		try {
			props.onUnfocused(null);
			if (!validateFn) return;
			const hasValidationError = hasValue(validationError) && !validationError.includes("has not been validated");
			if (hasValidationError || noValue(value) || validationResults.some((r) => r.value === value)) return;
			setSearching(true);
			const { success, message } = (await validateFn(id)) as ValidationResponse;
			const newResults = { value, success, message };
			setValidationResults([...validationResults, newResults]);
		} catch (ex) {
			console.error(ex);
		} finally {
			setSearching(false);
		}
	}

	return (
		<>
			<TextBox
				{...props}
				onUnfocused={validate}
				changeHandler={onChange}
				rightAddon={RightAddOn()}
				disabled={disabled || readOnly || searching}
				validationError={searching ? undefined : validationError}
			/>
		</>
	);

	function RightAddOn() {
		if (searching) {
			return (
				<div className={style.spinner}>
					<span>Validating...</span>
					<FontAwesomeIcon icon={faCircleNotch} color="blue" spin />
				</div>
			);
		}

		if (success) {
			return (
				<div>
					<FontAwesomeIcon icon={faCheckCircle} color="green" />
					<span className={style.text}>Validated</span>
				</div>
			);
		}

		return null;
	}
};
